import { useMemo, useState } from 'react';
import { ArrowLeft, Award, Search, Eye, Download, Copy, FileSpreadsheet, Building2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/hooks/use-toast';
import * as XLSX from 'xlsx';
import { useGeneratedCertificates, type GeneratedCertificateRow } from '@/hooks/useGeneratedCertificates';
import { generateDiplomaPDF, buildPublicCertUrl } from '@/lib/generateDiplomaPDF';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface Props {
  selectedOrg: string;
  onOrgChange: (org: string) => void;
  onBack: () => void;
}

export function GeneratedCertificatesView({ selectedOrg, onOrgChange, onBack }: Props) {
  const { currentRole } = useAuth();
  const isSuperAdmin = currentRole === 'super_admin';
  const { data: certs = [], isLoading } = useGeneratedCertificates(selectedOrg);

  const [search, setSearch] = useState('');
  const [filterProgram, setFilterProgram] = useState('all');
  const [filterCohort, setFilterCohort] = useState('all');
  const [filterDiploma, setFilterDiploma] = useState('all');

  const { data: organizations = [] } = useQuery({
    queryKey: ['organizations-cert-filter'],
    queryFn: async () => {
      const { data, error } = await supabase.from('organizations').select('id, name').order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: isSuperAdmin,
  });

  // derive program/cohort options from data
  const programs = useMemo(() => {
    const map = new Map<string, string>();
    certs.forEach((c) => c.program && map.set(c.program.id, c.program.name));
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [certs]);

  const cohorts = useMemo(() => {
    const map = new Map<string, { id: string; name: string; program_id: string | null }>();
    certs.forEach((c) => {
      if (c.cohort) map.set(c.cohort.id, { id: c.cohort.id, name: c.cohort.name, program_id: c.program_id });
    });
    let list = Array.from(map.values());
    if (filterProgram !== 'all') list = list.filter((c) => c.program_id === filterProgram);
    return list;
  }, [certs, filterProgram]);

  const filtered = useMemo(() => {
    let list = certs;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          c.user_name?.toLowerCase().includes(q) ||
          c.user_email?.toLowerCase().includes(q) ||
          c.certificate_name?.toLowerCase().includes(q),
      );
    }
    if (filterProgram !== 'all') list = list.filter((c) => c.program_id === filterProgram);
    if (filterCohort !== 'all') list = list.filter((c) => c.cohort_id === filterCohort);
    if (filterDiploma !== 'all') list = list.filter((c) => (c.diploma_type ?? 'A') === filterDiploma);
    return list;
  }, [certs, search, filterProgram, filterCohort, filterDiploma]);

  // KPIs
  const kpis = useMemo(() => {
    const total = certs.length;
    const a = certs.filter((c) => (c.diploma_type ?? 'A') === 'A').length;
    const b = certs.filter((c) => c.diploma_type === 'B').length;
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const last30 = certs.filter((c) => new Date(c.generated_at).getTime() >= cutoff).length;
    return { total, a, b, last30 };
  }, [certs]);

  // Actions
  const handleView = (c: GeneratedCertificateRow) => {
    window.open(buildPublicCertUrl(c.id), '_blank');
  };

  const handleCopy = async (c: GeneratedCertificateRow) => {
    await navigator.clipboard.writeText(buildPublicCertUrl(c.id));
    toast({ title: 'Enlace copiado' });
  };

  const handleDownload = async (c: GeneratedCertificateRow) => {
    try {
      const pc = c.program_certificate || {};
      const type = (c.diploma_type ?? 'A') as 'A' | 'B';
      const prefix = type === 'A' ? 'diploma_a' : 'diploma_b';
      await generateDiplomaPDF({
        participantName: c.user_name || '',
        diplomaName: pc[`${prefix}_name`] || c.certificate_name || 'Certificado',
        certificateId: c.id,
        issuedAt: c.generated_at,
        backgroundImageUrl: pc[`${prefix}_image_url`] || null,
        publicUrl: buildPublicCertUrl(c.id),
        entities: pc[`${prefix}_entities`] || null,
      });
    } catch (err: any) {
      toast({ title: 'Error al generar PDF', description: err.message, variant: 'destructive' });
    }
  };

  const handleExportExcel = () => {
    const rows = filtered.map((c) => ({
      Participante: c.user_name,
      Email: c.user_email,
      Ruta: c.program?.name || '',
      Cohorte: c.cohort?.name || '',
      Organización: c.organization?.name || 'Pública',
      Certificado: c.certificate_name,
      Diploma: c.diploma_type ?? 'A',
      Generado: format(new Date(c.generated_at), 'yyyy-MM-dd HH:mm'),
      Descargas: c.download_count,
      'Enlace Público': buildPublicCertUrl(c.id),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Certificados');
    XLSX.writeFile(wb, `certificados-generados-${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onBack} className="gap-2">
              <ArrowLeft className="w-4 h-4" /> Volver
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
                <Award className="w-7 h-7 text-primary" /> Certificados Generados
              </h1>
              <p className="text-muted-foreground mt-1">Histórico global de diplomas emitidos</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isSuperAdmin && (
              <Select value={selectedOrg} onValueChange={onOrgChange}>
                <SelectTrigger className="w-[280px]">
                  <Building2 className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filtrar organización" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">🌐 Todas (incl. públicas)</SelectItem>
                  <SelectItem value="public">🎓 Solo cohortes públicas</SelectItem>
                  {organizations.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button variant="outline" className="gap-2" onClick={handleExportExcel}>
              <FileSpreadsheet className="w-4 h-4" /> Exportar
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total emitidos</p>
              <p className="text-2xl font-bold">{kpis.total}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Diplomas A</p>
              <p className="text-2xl font-bold">{kpis.a}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Diplomas B</p>
              <p className="text-2xl font-bold">{kpis.b}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Últimos 30 días</p>
              <p className="text-2xl font-bold">{kpis.last30}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4 grid gap-3 md:grid-cols-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterProgram} onValueChange={(v) => { setFilterProgram(v); setFilterCohort('all'); }}>
              <SelectTrigger><SelectValue placeholder="Ruta" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las rutas</SelectItem>
                {programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterCohort} onValueChange={setFilterCohort}>
              <SelectTrigger><SelectValue placeholder="Cohorte" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las cohortes</SelectItem>
                {cohorts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterDiploma} onValueChange={setFilterDiploma}>
              <SelectTrigger><SelectValue placeholder="Diploma" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                <SelectItem value="A">Diploma A</SelectItem>
                <SelectItem value="B">Diploma B</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-2">
                {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <Award className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No hay certificados que coincidan con los filtros.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Participante</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Ruta</TableHead>
                    <TableHead>Cohorte</TableHead>
                    <TableHead>Diploma</TableHead>
                    <TableHead>Generado</TableHead>
                    <TableHead className="text-center">Descargas</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((c) => {
                    const type = c.diploma_type ?? 'A';
                    return (
                      <TableRow key={c.id}>
                        <TableCell className="font-medium">{c.user_name}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{c.user_email}</TableCell>
                        <TableCell>{c.program?.name || '—'}</TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span>{c.cohort?.name || '—'}</span>
                            {!c.organization_id && (
                              <Badge variant="outline" className="text-[10px] py-0 mt-1 w-fit">Pública</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={type === 'A' ? 'bg-primary/20 text-primary border-primary/30' : 'bg-amber-500/20 text-amber-500 border-amber-500/30'}>
                            Diploma {type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {format(new Date(c.generated_at), 'dd MMM yyyy', { locale: es })}
                        </TableCell>
                        <TableCell className="text-center">{c.download_count}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="icon" variant="ghost" onClick={() => handleView(c)} title="Ver diploma">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => handleDownload(c)} title="Descargar PDF">
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => handleCopy(c)} title="Copiar enlace">
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
